import React, { useCallback, useMemo, useState } from 'react';

import { useSnackbar } from 'notistack';
import { useDropzone } from 'react-dropzone';

import UploadFileIcon from '@mui/icons-material/UploadFile';
import {
  Alert,
  AlertTitle,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  LinearProgress,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';

import { useUploadCostPrices } from '~/hooks/apis/tours/useTourCostPrices';

import { TourService } from '~/types/tours/costPrice';

import { formatAmount } from './formatAmount';
import {
  ParsedSlidingScaleRow,
  buildTemplateCsv,
  buildTemplateFilename,
  downloadCsv,
  parseSlidingScaleCsv,
} from './slidingScaleCsv';

interface Props {
  open: boolean;
  service: TourService;
  /** How many sliding scale rows the service already has, if any. */
  existingRowCount?: number;
  onClose: () => void;
}

export default function SlidingScaleUploadDialog({ open, service, existingRowCount = 0, onClose }: Props) {
  const { enqueueSnackbar } = useSnackbar();

  const [fileName, setFileName] = useState<string | null>(null);
  const [parseErrors, setParseErrors] = useState<Array<string>>([]);
  const [rows, setRows] = useState<Array<ParsedSlidingScaleRow>>([]);

  const uploadCostPrices = useUploadCostPrices({ supplierId: service.supplierId, serviceId: service.id });

  const reset = useCallback(() => {
    setFileName(null);
    setParseErrors([]);
    setRows([]);
  }, []);

  const handleClose = useCallback(() => {
    reset();
    onClose();
  }, [onClose, reset]);

  const onDrop = useCallback((files: Array<File>) => {
    const file = files[0];
    if (!file) {
      return;
    }

    const reader = new FileReader();
    reader.onload = () => {
      const parsed = parseSlidingScaleCsv(String(reader.result ?? ''));
      setFileName(file.name);
      setParseErrors(parsed.errors);
      setRows(parsed.rows);
    };
    reader.onerror = () => {
      setParseErrors(['Could not read the selected file']);
    };
    reader.readAsText(file);
  }, []);

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    multiple: false,
    accept: {
      'text/csv': ['.csv'],
      'application/vnd.ms-excel': ['.csv'],
    },
  });

  /**
   * svc-tour takes the whole sheet in one request and resolves package names
   * against the service's configurations, so rows are sent together rather than
   * one request per row.
   */
  const handleUpload = useCallback(async () => {
    if (!rows.length) {
      return;
    }

    try {
      await uploadCostPrices.mutateAsync({
        kind: 'SlidingScale',
        pricing: rows.map((row) => row.item),
      });

      enqueueSnackbar(`Uploaded ${rows.length} cost price row${rows.length > 1 ? 's' : ''}`, { variant: 'success' });
      handleClose();
    } catch (error) {
      enqueueSnackbar(`Upload failed: ${error?.message ?? 'Unknown error'}`, {
        variant: 'error',
        autoHideDuration: 10000,
      });
    }
  }, [enqueueSnackbar, handleClose, rows, uploadCostPrices]);

  const previewRows = useMemo(() => rows.slice(0, 20), [rows]);

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="lg" fullWidth>
      <DialogTitle>{existingRowCount > 0 ? 'Re-upload' : 'Upload'} sliding scale cost prices</DialogTitle>
      <DialogContent dividers>
        <Stack spacing={2}>
          <Stack direction="row" spacing={2} alignItems="center">
            <Button
              variant="text"
              onClick={() => downloadCsv(buildTemplateFilename(service), buildTemplateCsv(service))}
            >
              Download template
            </Button>
            <Typography variant="body2" color="text.secondary">
              Pre-filled with this service&apos;s packages. Add a row per season.
            </Typography>
          </Stack>

          <Box
            {...getRootProps()}
            sx={{
              p: 4,
              border: '1px dashed',
              borderColor: isDragActive ? 'primary.main' : 'divider',
              borderRadius: 1,
              textAlign: 'center',
              cursor: 'pointer',
              bgcolor: isDragActive ? 'action.hover' : 'background.default',
            }}
          >
            <input {...getInputProps()} />
            <UploadFileIcon color="action" />
            <Typography variant="body2">
              {fileName ?? 'Drop a cost price CSV here, or click to choose a file'}
            </Typography>
          </Box>

          {existingRowCount > 0 && (
            <Alert severity="warning">
              This service already has {existingRowCount} sliding scale row{existingRowCount === 1 ? '' : 's'}.
              Uploading replaces the pricing for any package and currency in the sheet, including seasons the sheet
              leaves out.
            </Alert>
          )}

          {uploadCostPrices.isPending && <LinearProgress />}

          {!!parseErrors.length && (
            <Alert severity="warning">
              <AlertTitle>{parseErrors.length} row(s) skipped</AlertTitle>
              <Stack component="ul" sx={{ pl: 2, m: 0 }}>
                {parseErrors.slice(0, 10).map((error) => (
                  <li key={error}>
                    <Typography variant="body2">{error}</Typography>
                  </li>
                ))}
              </Stack>
              {parseErrors.length > 10 && <Typography variant="body2">…and {parseErrors.length - 10} more</Typography>}
            </Alert>
          )}

          {!!rows.length && (
            <>
              <Typography variant="body2" color="text.secondary">
                {rows.length} row(s) ready to upload. Package and package option are matched by name against this
                service&apos;s configurations.
              </Typography>

              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Season</TableCell>
                    <TableCell>Package</TableCell>
                    <TableCell>PackageOption</TableCell>
                    <TableCell>Currency</TableCell>
                    <TableCell align="right">Single supplement</TableCell>
                    <TableCell align="right">Pax quoted</TableCell>
                    <TableCell>CSV row</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {previewRows.map(({ item, rowNumber }) => (
                    <TableRow key={rowNumber}>
                      <TableCell>{item.season}</TableCell>
                      <TableCell>{item.tourConfiguration}</TableCell>
                      <TableCell>{item.packageConfiguration}</TableCell>
                      <TableCell>{item.currency}</TableCell>
                      <TableCell align="right">{formatAmount(item.singleSupplement)}</TableCell>
                      <TableCell align="right">1–{item.scale.length}</TableCell>
                      <TableCell>{rowNumber}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>

              {rows.length > previewRows.length && (
                <Typography variant="body2" color="text.secondary">
                  …and {rows.length - previewRows.length} more row(s) not shown.
                </Typography>
              )}
            </>
          )}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button variant="contained" onClick={handleUpload} disabled={!rows.length || uploadCostPrices.isPending}>
          Upload {rows.length || ''} row{rows.length === 1 ? '' : 's'}
        </Button>
        <Button variant="text" onClick={handleClose} disabled={uploadCostPrices.isPending}>
          Cancel
        </Button>
      </DialogActions>
    </Dialog>
  );
}
